import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { card, input, money, pct } from './costingApi';
import { Card, CardContent } from '@/components/ui/card';

const SORTS = {
  realized_gross_profit: 'Highest gross profit',
  realized_margin_pct: 'Highest margin',
  days_to_recover: 'Fastest cost recovery',
  sell_through_pct: 'Slowest-moving stock',
  projected_gross_profit: 'Highest projected profit',
  purchase_date: 'Newest purchase',
};

const sortRows = (rows, key) => [...rows].sort((a, b) => {
  const av = a[key]; const bv = b[key];
  if (key === 'days_to_recover') return (av ?? 1e9) - (bv ?? 1e9);
  if (key === 'sell_through_pct') return (av ?? 0) - (bv ?? 0);
  if (key === 'purchase_date') return String(bv).localeCompare(String(av));
  return (bv ?? -1e12) - (av ?? -1e12);
});

const EngineCompare = ({ rows }) => {
  const groups = {};
  rows.forEach((r) => {
    const k = `${r.make || ''} ${r.model || ''} ${r.engine_type || ''}`.trim() || 'Batch';
    const g = (groups[k] ||= { n: 0, cost: 0, rev: 0, gp: 0, proj: 0 });
    g.n += 1; g.cost += r.total_cost; g.rev += r.sales_revenue; g.gp += r.realized_gross_profit; g.proj += r.projected_gross_profit;
  });
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="engine-compare">
      {Object.entries(groups).map(([k, g]) => (
        <div key={k} className="rounded border border-slate-700 bg-slate-900/60 p-3 text-sm">
          <p className="font-semibold capitalize">{k} <span className="text-slate-400 text-xs">({g.n})</span></p>
          <p className="text-xs text-slate-300">Avg cost {money(g.cost / g.n)} · Avg revenue {money(g.rev / g.n)}</p>
          <p className="text-xs">Avg realised GP <b className="text-emerald-400">{money(g.gp / g.n)}</b> · Avg projected GP {money(g.proj / g.n)}</p>
        </div>
      ))}
    </div>
  );
};

export const ProfitabilityTable = ({ report, onOpen }) => {
  const [sort, setSort] = useState('realized_gross_profit');
  const [engine, setEngine] = useState('all');
  const rows = useMemo(() => sortRows((report?.vehicles || []).filter((r) => engine === 'all' || r.engine_type === engine), sort), [report, sort, engine]);
  const t = report?.totals || {};
  const tiles = [
    ['Vehicles / batches', report?.vehicles?.length ?? 0], ['Acquisition cost', money(t.total_cost)], ['Sales revenue', money(t.sales_revenue)],
    ['COGS', money(t.cogs)], ['Realised gross profit', money(t.realized_gross_profit)], ['Remaining at cost', money(t.remaining_at_cost)],
    ['Remaining at selling', money(t.remaining_at_selling)], ['Projected gross profit', money(t.projected_gross_profit)],
    ['Unallocated cost', money(t.unallocated_cost)], ['Cost recovered', `${report?.recovered_count ?? 0} vehicles`],
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {tiles.map(([l, v]) => (
          <Card key={l} className={card}><CardContent className="p-3"><p className="text-[11px] text-slate-400">{l}</p><p className="font-bold" data-testid={`prof-tile-${l.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{v}</p></CardContent></Card>
        ))}
      </div>
      <EngineCompare rows={rows} />
      <div className="flex flex-wrap gap-2">
        <Select value={sort} onValueChange={setSort}><SelectTrigger className={`${input} w-56`} data-testid="prof-sort"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
        <Select value={engine} onValueChange={setEngine}><SelectTrigger className={`${input} w-40`} data-testid="prof-engine-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All engines</SelectItem><SelectItem value="petrol">Petrol</SelectItem><SelectItem value="diesel">Diesel</SelectItem></SelectContent></Select>
      </div>
      <div className="overflow-x-auto border border-slate-700 rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-800 text-slate-300"><tr>{['Vehicle', 'Purchased', 'Cost', 'Parts rec./sold', 'Revenue', 'COGS', 'Realised GP', 'Margin', 'Left @ cost', 'Left @ sell', 'Projected GP', 'Cash recovery', 'Recovered'].map((h) => <th key={h} className="p-2 text-left whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={r.id} className="border-t border-slate-700 hover:bg-slate-800 cursor-pointer" onClick={() => onOpen(r.id)} data-testid={`prof-row-${r.id}`}>
              <td className="p-2 whitespace-nowrap"><span className="text-slate-500 mr-1">#{i + 1}</span><b>{r.acq_no}</b> {r.make} {r.model} <span className="capitalize text-slate-400">{r.engine_type}</span> {r.registration && <span className="font-mono text-slate-400">{r.registration}</span>}
                {r.allocation_status !== 'finalized' && <Badge className="ml-1 bg-amber-600 text-[9px]">provisional</Badge>}
                {r.sales_needing_cost_review > 0 && <Badge className="ml-1 bg-rose-600 text-[9px]">{r.sales_needing_cost_review} need cost</Badge>}</td>
              <td className="p-2 whitespace-nowrap">{r.purchase_date}<div className="text-slate-500">{r.days_since_purchase} days</div></td>
              <td className="p-2">{money(r.total_cost)}</td>
              <td className="p-2">{r.parts_recovered} / {r.parts_sold}<div className="text-slate-500">{pct(r.sell_through_pct)}</div></td>
              <td className="p-2">{money(r.sales_revenue)}</td>
              <td className="p-2">{money(r.cogs)}</td>
              <td className={`p-2 font-semibold ${r.realized_gross_profit < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{money(r.realized_gross_profit)}</td>
              <td className="p-2">{pct(r.realized_margin_pct)}</td>
              <td className="p-2">{money(r.remaining_at_cost)}</td>
              <td className="p-2">{money(r.remaining_at_selling)}</td>
              <td className="p-2">{money(r.projected_gross_profit)}</td>
              <td className="p-2">{pct(r.cash_recovery_pct)}</td>
              <td className="p-2 whitespace-nowrap">{r.recovered_on ? <span className="text-emerald-400">{r.recovered_on}<div className="text-slate-500">{r.days_to_recover} days</div></span> : <span className="text-slate-500">not yet</span>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={13} className="p-4 text-center text-slate-400">No acquisitions yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">Cash recovery = cumulative sales ÷ acquisition cost (cash view). Realised gross profit = sales − COGS of parts sold (accounting view). They differ because recovery ignores the cost still sitting in unsold parts.</p>
    </div>
  );
};
